import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { WorkbookDetailsDialogCtrl } from '../../../ts/controllers/WorkbookDetailsDialogCtrl';
import { WorkbookPublishResultService } from '../../../ts/controllers/WorkbookPublishResultCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { Filter } from '../../../ts/helpers/Filter';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { WorkbookDefinition } from '../../../ts/models/ContentDefinitions';
import { ContentTypeFilterKey } from '../../../ts/models/ContentFilters';
import { WorkbookContentType } from '../../../ts/models/ContentTypes';
import { IWorkbookDetailInfo, IWorkbookInfo } from '../../../ts/models/IWorkbookInfo';
import { Projects } from '../../../ts/resources/Projects';
import { Workbooks } from '../../../ts/resources/Workbooks';
import { FavoriteItemType } from '../../../ts/ServerApi/types/IFavorite';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { IWorkbookActions } from '../../../ts/ServerApi/types/IWorkbookActions';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { DataCatalogApiRequestsService } from '../../../ts/services/DataCatalogApiRequestsService';
import { ExtractActionStatusService } from '../../../ts/services/ExtractActionStatusService';
import { ModalService } from '../../../ts/services/ModalService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../actions/spaces/FavoritesActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../../actions/spaces/SetPreviousUrlReducerAction';
import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs } from '../../content/breadcrumbs/BreadcrumbHelpers';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { SpaceInfo, WorkbookSubspaceName } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { DetailsDialogInAngularPlaceHelper } from '../DetailsDialog/AngularContentDetailsDialog';
import { getContentTypesFromQueryParam } from '../Explorer/ExplorerUrlBindings';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { RequestAccessError } from '../RouterHelpers/RequestAccessError';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedWorkbookSpace } from './ConnectedWorkbookSpace';
import { WorkbookSpaceOwnProps } from './WorkbookSpace';

export interface WorkbookSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly routeLocationSearch: string;
  readonly subspaceName: WorkbookSubspaceName;
  readonly workbookId: string;
  readonly publishResultDisplay: boolean;
  readonly publishResultUrl: string;
  readonly urls: SpacesSiteUrls;
  readonly renderWorkbookSpace: (props: WorkbookSpaceOwnProps) => JSX.Element;
  readonly fetchedAllData: () => void;
}

export interface WorkbookSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly addContentFavorite: (contentId: string, contentType: FavoriteItemType) => void;
  readonly removeContentFavorite: (contentId: string, contentIdForFavoriting: string, contentType: FavoriteItemType, contentName: string) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
}

export interface WorkbookSpaceProviderPropsFromAngular {
  readonly workbooks: Workbooks;
  readonly telemetry: Telemetry;
  readonly sanitizer: ng.sanitize.ISanitizeService;
  readonly modalService: ModalService;
  readonly q: ng.IQService;
  readonly browserTitleService: BrowserTitleService;
  readonly projects: Projects;
  readonly workbookPublishResultService: WorkbookPublishResultService;
  readonly dataCatalogApiRequestsService: DataCatalogApiRequestsService;
  readonly extractActionStatusService: ExtractActionStatusService;
}

export interface WorkbookSpaceProviderStateProps {
  readonly isRequestAccessEnabled: boolean;
  readonly isWebExtractionEnabled: boolean;
}

export type WorkbookSpaceNonAngularProps =  WorkbookSpaceProviderStateProps & WorkbookSpaceProviderOwnProps & WorkbookSpaceProviderDispatchProps;

export type WorkbookSpaceProviderContainerProps =  WorkbookSpaceProviderStateProps & WorkbookSpaceProviderOwnProps &  WorkbookSpaceProviderDispatchProps & WorkbookSpaceProviderPropsFromAngular;

export interface WorkbookSpaceProviderContainerState {
  readonly workbookActions: IWorkbookActions;
  readonly workbookInfo: IWorkbookInfo;
  readonly workbookDetailInfo: IWorkbookDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly errorCode: Number;
}

const InitialState: WorkbookSpaceProviderContainerState = {
  workbookActions: undefined,
  workbookDetailInfo: undefined,
  workbookInfo: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false,
  errorCode: undefined
};

export class WorkbookSpaceProviderContainer extends React.PureComponent<WorkbookSpaceProviderContainerProps, WorkbookSpaceProviderContainerState> {

  private detailsDialogHelper: DetailsDialogInAngularPlaceHelper;
  private unmounted: boolean = false;

  public constructor(props: WorkbookSpaceProviderContainerProps){
    super(props);
    this.state = InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderWorkbookSpace({
        workbookDetail: {
          usageInfo: this.state.workbookInfo.usageInfo,
          usageInfoMaxValues: this.state.workbookInfo.usageInfoMaxValues,
          ...this.state.workbookDetailInfo
        },
        workbookActions: this.state.workbookActions,
        openDetail: this.openDetail,
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite,
        sanitizer: this.props.sanitizer,
        telemetry: this.props.telemetry,
        dataCatalogApiRequestsService: this.props.dataCatalogApiRequestsService,
        angularContext: this.props.angularContext
      });
    } else if (this.state.hasError) {
      if (this.props.isRequestAccessEnabled) {
        return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.workbookId} contentType={WorkbookContentType} errorCode={this.state.errorCode}/>;
      }
      return <RouterError errorType={'workbooks'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: WorkbookSpaceProviderContainerProps) {
    if (this.changedWorkbook(prevProps)) {
      this.updateState(InitialState /* want the initial state for the new workbook */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace(false);

      this.detailsDialogHelper = new DetailsDialogInAngularPlaceHelper(
        this.getData,
        this.props.q,
        this.props.angularContext,
        this.props.modalService,
        this.props.telemetry
      );

      this.setState({initialRender: false, renderChildren: true}, () => {
        if (this.props.publishResultDisplay) {
          this.props.workbookPublishResultService.handlePublishedWorkbook(this.state.workbookDetailInfo, this.props.publishResultUrl);
          this.removePublishDialogUrlParams();
        }
      });

      this.props.workbooks.markRecentlyViewed(this.state.workbookDetailInfo.id);
      this.props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.workbookDetailInfo.name });

    } else if (prevProps.subspaceName !== this.props.subspaceName) {
      this.changeSubspace(true);
    }
  }

  private getData = () => {
    this.getWorkbook();
    this.getWorkbookDetail();
    this.getActions();
  }

  private changedWorkbook(prevProps: WorkbookSpaceProviderContainerProps) {
    return !_.isUndefined(prevProps.workbookId)
      && !_.isUndefined(this.props.workbookId)
      && prevProps.workbookId !== this.props.workbookId;
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.workbookInfo) &&
      !_.isUndefined(this.state.workbookDetailInfo) &&
      !_.isUndefined(this.state.workbookActions);
  }

  private getWorkbookDetail = async () => {
    try {
      const workbookDetail = await this.props.workbooks.getById(this.props.workbookId);
      this.updateState({workbookDetailInfo: workbookDetail});
      const projectHierarchy = await this.props.projects.getProjectHierarchy(workbookDetail.project.id);
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      populateContentBreadcrumbs([ ...projectHierarchyWithType, workbookDetail ], this.props.urls);
      this.props.fetchedAllData();
      // if the extract create feature disabled, we won't tigger the poll service.
      if (this.props.isWebExtractionEnabled) {
        this.pollAndUpdateExtractPendingStatus();
      }
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as Number});
    }
  }

  private getWorkbook = async () => {
    try {
      const workbook = await this.props.workbooks.all()
        .withFilters([Filter.fieldEq('id', this.props.workbookId)])
        .getFirst();
      this.updateState({workbookInfo: workbook});
    } catch (e) {
      this.updateState({hasError: true, errorCode: e as Number});
    }
  }

  private getActions = async () => {
    try {
      const actions = await this.props.workbooks.actions(this.props.workbookId);
      this.updateState({workbookActions: actions});
    } catch (e) {}
  }

  private pollAndUpdateExtractPendingStatus = async () => {
    try {
      this.props.extractActionStatusService.pollAndUpdateExtractActionStatus(this.state.workbookInfo);
      // if polling failed will do nothing
    } catch (e) {}
  }


  private updateState<K extends keyof WorkbookSpaceProviderContainerState>(newState: Pick<WorkbookSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }

  private changeSubspace = (shouldNotFetchCounts: boolean) => {
    this.props.enterSpace(
      {
        item: this.state.workbookDetailInfo,
        spaceType: WorkbookDefinition.contentType,
        subspaceName: getEffectiveWorkbookSubspaceName(this.props.subspaceName, queryString.parse(this.props.routeLocationSearch)),
      }
    );

    // If we just switched tabs we know content counts are up to date.
    // They will get set to false by the above EnterSpaceAction.
    if(shouldNotFetchCounts) {
      this.props.contentCountsUpToDate();
    }

    this.props.requestSpaceContents();
  }

  private openDetail = () => {
    const { workbookActions, workbookDetailInfo, workbookInfo } = this.state;
    this.detailsDialogHelper.openDialog(
      this.state.workbookDetailInfo,
      {
        controller: WorkbookDetailsDialogCtrl,
        resolve: {
          workbookActions: () => workbookActions,
          workbookInfo: () => workbookInfo,
          workbookDetailInfo: () => workbookDetailInfo,
          detailsDialogHelper: () => this.detailsDialogHelper
        }
      }
    );
  }

  /**
   * Prevents the publish dialog from showing up multiple times because of state.hardReload
   * re-rendering this react component and the previousUrl being the same in the app state.
   */
  private removePublishDialogUrlParams = () => {
    const cleanWorkbookUrl = window.location.href;
    this.props.setPreviousUrl(cleanWorkbookUrl);
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.workbookDetailInfo.id, 'workbook');
  }

  private unfavorite = () => {
   this.props.removeContentFavorite(this.state.workbookDetailInfo.id, this.state.workbookDetailInfo.id, 'workbook', this.state.workbookDetailInfo.name);
  }
}

export namespace WorkbookSpaceProviderAngularProvider {

  function getAngularServicesWithServiceLocator() {
    const workbooks = ServiceLocator.get<Workbooks>('Workbooks');
    const telemetry = ServiceLocator.get<Telemetry>('TelemetryService');
    const sanitizer = ServiceLocator.get<ng.sanitize.ISanitizeService>('$sanitize');
    const modalService = ServiceLocator.get<ModalService>('ModalService');
    const q = ServiceLocator.get<ng.IQService>('$q');
    const browserTitleService = ServiceLocator.get<BrowserTitleService>('BrowserTitleService');
    const projects = ServiceLocator.get<Projects>('Projects');
    const workbookPublishResultService = ServiceLocator.get<WorkbookPublishResultService>('WorkbookPublishResultService');
    const dataCatalogApiRequestsService = ServiceLocator.get<DataCatalogApiRequestsService>('DataCatalogApiRequestsService');
    const extractActionStatusService = ServiceLocator.get<ExtractActionStatusService>('ExtractActionStatusService');
    return {
      workbooks,
      telemetry,
      sanitizer,
      modalService,
      q,
      browserTitleService,
      projects,
      workbookPublishResultService,
      dataCatalogApiRequestsService,
      extractActionStatusService
    };
  }

  function selectPropsFromAngularServices(angularServices: WorkbookSpaceProviderPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<WorkbookSpaceNonAngularProps, WorkbookSpaceProviderPropsFromAngular, WorkbookSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const WorkbookSpaceProviderAng: React.ComponentType<WorkbookSpaceNonAngularProps> = WorkbookSpaceProviderAngularProvider.HOC(WorkbookSpaceProviderContainer);

const mapStateToProps: MapStateToProps<WorkbookSpaceProviderStateProps> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state),
  isWebExtractionEnabled: SessionSiteSelectors.isWebExtractionEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<WorkbookSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterSpace,
  setOrderAction,
  addContentFavorite,
  removeContentFavorite,
  setPreviousUrl,
}, dispatch);

export const ConnectedWorkbookSpaceProviderContainer: React.ComponentClass<WorkbookSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(WorkbookSpaceProviderAng);

const renderWorkbookSpace = (props: WorkbookSpaceOwnProps): JSX.Element => <ConnectedWorkbookSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type WorkbookSpaceProviderProps = LocationPathProp & Omit<WorkbookSpaceProviderOwnProps, 'renderWorkbookSpace' | 'fetchedAllData'>;

export interface WorkbookSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export class WorkbookSpaceProvider extends React.PureComponent<WorkbookSpaceProviderProps, WorkbookSpaceProviderState> {
  public constructor(props: WorkbookSpaceProviderProps) {
    super(props);
    this.state = {fetchedAllData: false};
  }

  public render() {
    const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
      routeLocationPath: this.props.routeLocationPath,
      routeLocationSearch: this.props.routeLocationSearch,
      shouldUpdateContentTypes: true,
      shouldUpdateFilters: false,
      shouldUpdateSort: true,
      shouldUpdateBreadcrumbs: this.state.fetchedAllData
    };

    const WorkbookSpaceInfo: SpaceInfo = {
      item: null,
      spaceType: 'workbook',
      subspaceName: getEffectiveWorkbookSubspaceName(this.props.subspaceName, queryString.parse(this.props.routeLocationSearch)),
    };

    return (
      <ConnectedSpaceInfoProvider spaceInfo={WorkbookSpaceInfo}>
        <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
          <ConnectedWorkbookSpaceProviderContainer {...{...this.props, renderWorkbookSpace, fetchedAllData: this.fetchedAllData}} />
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
  }

  private fetchedAllData = () => {
    this.setState({fetchedAllData: true});
  }
}

function getEffectiveWorkbookSubspaceName(
  subspaceName: WorkbookSubspaceName,
  queryParams: { [i: string]: string },
): WorkbookSubspaceName {
  // Check if we're in dataconnection subspace.
  // This means we are in the datasources path and the content type filter is equal to dataconnection
  if (subspaceName === 'datasources') {
    const contentTypesParam = queryParams[ContentTypeFilterKey];
    const contentTypes = getContentTypesFromQueryParam(contentTypesParam, []);
    if (contentTypes.length === 1 && contentTypes[0] === 'dataconnection') {
      return 'dataconnections';
    }
  }

  return subspaceName;
}
